import { FC, memo, useState, useEffect, useMemo } from 'react';
import Icon from '@ant-design/icons';
import dayjs from 'dayjs';
import Image from 'next/image';
import styles from './ PromotionHeader.module.scss';
import DiscountIcon from '@/public/icon/Discount.svg';
import ClockIcon from '@/public/icon/ClockBlack.svg';
import { PromotionHeaderProps } from './ PromotionHeader.types';
import { useLanguage } from '@/hooks/use-language';
import classNames from 'classnames';
import { useQuoteCardMobile } from '@/hooks/use-quote-card-mobile';

const PromotionHeader = memo((props: PromotionHeaderProps) => {
  const { productIdentifier, bannerDesktop, bannerMobile, promotion, loading } = props;
  const language = useLanguage();
  const [day, setDay] = useState<number | string>('00');
  const [hour, setHour] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const [seconds, setSeconds] = useState('00');
  const [isGreaterThanSevenDays, setIsGreaterThanSevenDays] = useState<boolean>(false);
  const isQuoteCardMobile = useQuoteCardMobile();

  const getTime = () => {
    const formatTime = (time: number) => {
      let timeStr = time.toString();
      if (timeStr.length === 1) {
        return `0${timeStr}`;
      } else {
        return timeStr;
      }
    };
    const endTime = dayjs(promotion?.endDate);
    const currentTime = dayjs();
    const duration = dayjs.duration(endTime.diff(currentTime));
    const day =
      dayjs(endTime.format('YYYY-MM-DD')).diff(dayjs().format('YYYY-MM-DD'), 'day') || '00';
    const hour = formatTime(duration.hours());
    const minutes = formatTime(duration.minutes());
    const seconds = formatTime(duration.seconds());

    return {
      day,
      hour,
      minutes,
      seconds
    };
  };

  useEffect(() => {
    const currentTime = dayjs();
    const endDate = dayjs(promotion?.endDate);
    const diffInSeconds = endDate.diff(currentTime, 'seconds');
    setIsGreaterThanSevenDays(diffInSeconds > 7 * 24 * 60 * 60);
    const timer = setInterval(() => {
      const { day, hour, minutes, seconds } = getTime();
      setDay(day);
      setHour(hour);
      setMinutes(minutes);
      setSeconds(seconds);
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [promotion]);

  const promotionCountDown = useMemo(() => {
    return (
      <>
        {isGreaterThanSevenDays && promotion?.endDate && (
          <div className={styles.promotion_end_date}>
            <Icon component={ClockIcon} className={styles.promotion_clock_icon} />
            <span>優惠至</span>
            <span>{dayjs(promotion?.endDate).format('YYYY年MM月DD日')}</span>
          </div>
        )}
        {!isGreaterThanSevenDays && promotion?.endDate && (
          <div className={styles.promotion_end_date_count_down}>
            <Icon component={ClockIcon} className={styles.promotion_clock_icon} />
            <span className={styles.count_down_label}>
              {language === 'zh' ? '限時倒數' : 'Ends in'}
            </span>
            <div className={styles.count_down_item}>
              <div className={styles.count}>{day}</div>
              <span className={styles.count_name}>{language === 'zh' ? '日' : 'd'}</span>
            </div>
            <div className={styles.count_down_item}>
              <div className={styles.count}>{hour}</div>
              <span className={styles.count_name}>{language === 'zh' ? '小時' : 'h'}</span>
            </div>

            <div className={styles.count_down_item}>
              <div className={styles.count}>{minutes}</div>
              <span className={styles.count_name}>{language === 'zh' ? '分' : 'm'}</span>
            </div>
            <div className={styles.count_down_item}>
              <div className={styles.count}>{seconds}</div>
              <span className={styles.count_name}>{language === 'zh' ? '秒' : 's'}</span>
            </div>
          </div>
        )}
      </>
    );
  }, [isGreaterThanSevenDays, promotion?.endDate, language, day, hour, minutes, seconds]);

  return (
    <>
      <div
        className={classNames(styles.promotion_header_container)}
        style={{
          backgroundImage: `url(${isQuoteCardMobile ? bannerMobile?.url : bannerDesktop?.url})`
        }}
      >
        <div className={classNames(styles.promotion_product_banner)}>
          <div className={styles.promotion_banner_title}>
            <div className={styles.promotion_banner_btn_title}>立即報價</div>
            <div className={styles.promotion_banner_product_name}>
              {productIdentifier?.product_name}
            </div>
          </div>
          {!isQuoteCardMobile && promotion?.endDate && !loading && (
            <div className={styles.promotion_content}>
              <div className={styles.promotion_short_desc}>
                <Icon component={DiscountIcon} className={styles.promotion_icon} />
                {promotion?.shortDescZh}
              </div>
              {promotionCountDown}
            </div>
          )}
        </div>
      </div>
      {isQuoteCardMobile && promotion?.endDate && !loading && (
        <div className={styles.promotion_content}>
          <div className={styles.promotion_description}>
            <div className={styles.promotion_short_desc}>
              <Icon component={DiscountIcon} className={styles.promotion_icon} />
              {promotion?.shortDescZh}
            </div>
          </div>
          {promotionCountDown}
        </div>
      )}
    </>
  );
});
export default PromotionHeader;
